import PropTypes from "prop-types";
import React, { useState, useEffect, useCallback } from "react";
import BottomSheetModal from "./BottomSheetModal";
import { StepHeader, InfoRow, SuccessIcon } from "./SheetPrimitives";
import CustomButton from "../../components/AuthenticationComponents/CustomButton";
import HorizontalScroll from "../../components/Common/HorizontalScroll";

import searchIcon from "../../assets/images/user-dashboard/search.svg";
import orangeArrowIcon from "../../assets/images/user-dashboard/orange-arrow-right.svg";
import fallbackImage from "../../assets/images/user-dashboard/image.svg";
import ticketIcon from "../../assets/images/wallet/ticket-star.svg";
import giftIcon from "../../assets/images/wallet/gift-white.svg";
import arrowRightIconAuth from "../../assets/images/authentication/arrow-right.svg";

//Import API
import endpoints from "../../api/endpoints";
import { get, post } from "../../api/axios";

//Utils
import { extractErrorMessage } from "../../utils/errorHandler";

//Toast notifications
import toastr from "toastr";
import "toastr/build/toastr.min.css";

/* -------------------------------------------------------------------------
 * RestaurantOptionRow — one selectable restaurant in the redeem flow.
 * ---------------------------------------------------------------------- */

const RestaurantOptionRow = ({ restaurant, selected, onSelect, imageError, onImageError }) => {
  const [imgSrc, setImgSrc] = useState(restaurant.image);

  const handleImageError = () => {
    if (imgSrc !== fallbackImage) {
      setImgSrc(fallbackImage);
      onImageError && onImageError(restaurant.id);
    }
  };

  return (
    <div
      className={`mc-select-row mc-select-row--clickable ${
        selected ? "mc-select-row--selected" : ""
      }`}
      onClick={onSelect}
      role="button"
      tabIndex={0}
    >
      <span className="mc-select-row__thumb">
        <img src={imgSrc} alt={restaurant.name} onError={handleImageError} />
      </span>
      <div className="mc-select-row__body">
        <p className="mc-select-row__title">{restaurant.name}</p>
        <p className="mc-select-row__subtitle">
          {restaurant.category} &bull; {restaurant.location}
        </p>
      </div>
      <button
        type="button"
        className="mc-pill mc-pill--select"
        onClick={(e) => {
          e.stopPropagation();
          onSelect();
        }}
      >
        Select
        <img src={orangeArrowIcon} alt="" />
      </button>
    </div>
  );
};

RestaurantOptionRow.propTypes = {
  restaurant: PropTypes.shape({
    id: PropTypes.oneOfType([PropTypes.string, PropTypes.number]).isRequired,
    name: PropTypes.string.isRequired,
    image: PropTypes.string.isRequired,
    category: PropTypes.string.isRequired,
    location: PropTypes.string.isRequired,
  }).isRequired,
  selected: PropTypes.bool.isRequired,
  onSelect: PropTypes.func.isRequired,
  imageError: PropTypes.bool,
  onImageError: PropTypes.func,
};

/* -------------------------------------------------------------------------
 * VoucherOptionRow — one selectable voucher tier.
 * ---------------------------------------------------------------------- */

const VoucherOptionRow = ({ option, selected, onSelect }) => {
  const { amount, pointsCost, affordable, pointsNeeded } = option;

  return (
    <div
      className={`mc-select-row ${selected ? "mc-select-row--selected" : ""} ${
        affordable ? "mc-select-row--clickable" : "mc-select-row--disabled"
      }`}
      onClick={affordable ? onSelect : undefined}
      role={affordable ? "button" : undefined}
      tabIndex={affordable ? 0 : undefined}
    >
      <span className="mc-select-row__thumb mc-select-row__thumb--icon">
        <img src={ticketIcon} alt="" />
      </span>
      <div className="mc-select-row__body">
        <p className="mc-select-row__title">${amount} Voucher</p>
        <p className="mc-select-row__subtitle">Valid at selected restaurant</p>
      </div>
      <div className="mc-select-row__right">
        {affordable ? (
          <>
            <span className="mc-pill mc-pill--muted">
              {pointsCost.toLocaleString()} pts
            </span>
            <button
              type="button"
              className="mc-pill mc-pill--select"
              onClick={(e) => {
                e.stopPropagation();
                onSelect();
              }}
            >
              Select
              <img src={orangeArrowIcon} alt="" />
            </button>
          </>
        ) : (
          <div>
            <span className="mc-pill mc-pill--muted">
              {pointsCost.toLocaleString()} pts
            </span>
            <span className="mc-select-row__need-more">
              Need {pointsNeeded.toLocaleString()} more pts
            </span>
          </div>
        )}
      </div>
    </div>
  );
};

VoucherOptionRow.propTypes = {
  option: PropTypes.shape({
    id: PropTypes.oneOfType([PropTypes.string, PropTypes.number]).isRequired,
    amount: PropTypes.oneOfType([PropTypes.string, PropTypes.number]).isRequired,
    pointsCost: PropTypes.number.isRequired,
    affordable: PropTypes.bool.isRequired,
    pointsNeeded: PropTypes.number,
  }).isRequired,
  selected: PropTypes.bool.isRequired,
  onSelect: PropTypes.func.isRequired,
};

/* -------------------------------------------------------------------------
 * RedeemVoucherModal — Unified multi-step redemption modal.
 * Step 1: Select Restaurant
 * Step 2: Select Voucher Tier
 * Step 3: Success Confirmation
 * ---------------------------------------------------------------------- */

const RedeemVoucherModal = ({ isOpen, onClose, userPoints, onVoucherCreated, onNavigateToWallet, preSelectedRestaurant }) => {
  const [currentStep, setCurrentStep] = useState(1);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  // Step 1: Restaurant selection state
  const [restaurants, setRestaurants] = useState([]);
  const [categories, setCategories] = useState([]);
  const [categoriesCurrentPage, setCategoriesCurrentPage] = useState(1);
  const [categoriesHasMore, setCategoriesHasMore] = useState(true);
  const [categoriesLoadingMore, setCategoriesLoadingMore] = useState(false);
  const [activeCategory, setActiveCategory] = useState("all");
  const [searchValue, setSearchValue] = useState("");
  const [debouncedSearchValue, setDebouncedSearchValue] = useState("");
  const [selectedRestaurant, setSelectedRestaurant] = useState(null);

  // Pagination state
  const [currentPage, setCurrentPage] = useState(1);
  const [hasMore, setHasMore] = useState(true);
  const [loadingMore, setLoadingMore] = useState(false);

  // Step 2: Voucher selection state
  const [redemptionTiers, setRedemptionTiers] = useState([]);
  const [selectedVoucherTier, setSelectedVoucherTier] = useState(null);

  // Step 3: Success state
  const [redemptionResult, setRedemptionResult] = useState(null);

  // Image error handling for categories and restaurants
  const [categoryImageErrors, setCategoryImageErrors] = useState({});
  const [restaurantImageErrors, setRestaurantImageErrors] = useState({});
  const categorySentinelRef = React.useRef(null);
  const restaurantSentinelRef = React.useRef(null);

  const handleCategoryImageError = (categoryId) => {
    setCategoryImageErrors((prev) => ({ ...prev, [categoryId]: true }));
  };

  const handleRestaurantImageError = (restaurantId) => {
    setRestaurantImageErrors((prev) => ({ ...prev, [restaurantId]: true }));
  };

  // Configure toastr options
  toastr.options = {
    positionClass: "toast-bottom-left",
    timeOut: 5000,
    closeButton: true,
    progressBar: true,
  };

  // Calculate distance helper
  const calculateDistance = (lat1, lon1, lat2, lon2) => {
    const R = 3959; // Earth's radius in miles
    const dLat = ((lat2 - lat1) * Math.PI) / 180;
    const dLon = ((lon2 - lon1) * Math.PI) / 180;
    const a =
      Math.sin(dLat / 2) * Math.sin(dLat / 2) +
      Math.cos((lat1 * Math.PI) / 180) *
        Math.cos((lat2 * Math.PI) / 180) *
        Math.sin(dLon / 2) *
        Math.sin(dLon / 2);
    const c = 2 * Math.atan2(Math.sqrt(a), Math.sqrt(1 - a));
    return (R * c).toFixed(1);
  };

  // Transform API restaurant data to component format
  const transformRestaurantData = useCallback((apiRestaurant) => {
    const categories = apiRestaurant.categories || [];
    const firstCategory = categories[0];

    const isValidImageUrl = (url) => {
      if (!url) return false;
      if (
        url.includes("encrypted-tbn0.gstatic.com") &&
        url.includes("q=tbn:")
      ) {
        return false;
      }
      return true;
    };

    const bannerImage = isValidImageUrl(apiRestaurant.banner_url)
      ? apiRestaurant.banner_url
      : fallbackImage;

    const restaurantLat =
      apiRestaurant.latitude || apiRestaurant.address?.latitude;
    const restaurantLng =
      apiRestaurant.longitude || apiRestaurant.address?.longitude;
    
    const userLat = 14.4583647;
    const userLng = 100.131481;
    
    const distance =
      restaurantLat && restaurantLng
        ? `${calculateDistance(userLat, userLng, restaurantLat, restaurantLng)} mi`
        : null;

    return {
      id: apiRestaurant.id,
      name: apiRestaurant.title,
      image: bannerImage,
      category: firstCategory?.title || "Restaurant",
      location: distance || "Nearby",
    };
  }, []);

  // Fetch categories from API
  const fetchCategories = useCallback(async (page = 1, append = false) => {
    try {
      const response = await get(`${endpoints.categories}?page=${page}`);
      const apiCategories = response.data.data?.data || [];
      const pagination = response.data.data;

      const transformedCategories = apiCategories.map((cat) => ({
        id: cat.id.toString(),
        label: cat.title,
        icon: cat.icon || cat.avatar_icon,
      }));

      if (append) {
        setCategories((prev) => [...prev, ...transformedCategories]);
      } else {
        setCategories([
          { id: "all", label: "All", icon: "mdi-view-grid-outline" },
          ...transformedCategories,
        ]);
      }

      setCategoriesCurrentPage(pagination?.current_page || page);
      setCategoriesHasMore(pagination?.current_page < pagination?.last_page);
    } catch (err) {
      console.error("Failed to fetch categories:", err);
      if (!append) {
        setCategories([{ id: "all", label: "All", icon: "mdi-view-grid-outline" }]);
      }
    }
  }, []);

  // Load more categories (pagination)
  const loadMoreCategories = useCallback(async () => {
    if (categoriesLoadingMore || !categoriesHasMore) return;

    setCategoriesLoadingMore(true);
    const nextPage = categoriesCurrentPage + 1;
    await fetchCategories(nextPage, true);
    setCategoriesLoadingMore(false);
  }, [categoriesLoadingMore, categoriesHasMore, categoriesCurrentPage, fetchCategories]);

  // Fetch restaurants from API
  const fetchRestaurants = useCallback(async (page = 1, append = false) => {
    if (append) {
      setLoadingMore(true);
    } else {
      setLoading(true);
    }
    setError(null);
    try {
      const params = new URLSearchParams({
        is_member: "1",
        page_size: "20",
        page: page.toString(),
      });

      if (activeCategory !== "all") {
        params.append("category_id", activeCategory);
      }

      if (debouncedSearchValue.trim()) {
        params.append("search_by", debouncedSearchValue.trim());
      }

      const response = await get(`${endpoints.restaurantList}?${params.toString()}`);
      const transformedData = response.data.data.map(transformRestaurantData);
      
      if (append) {
        setRestaurants((prev) => [...prev, ...transformedData]);
      } else {
        setRestaurants(transformedData);
      }
      
      // Check if there are more pages
      const totalCount = response.data.meta?.total || 0;
      const hasMorePages = transformedData.length + (append ? restaurants.length : 0) < totalCount;
      setHasMore(hasMorePages);
      setCurrentPage(page);
    } catch (err) {
      console.error("Failed to fetch restaurants:", err);
      const errorMessage = extractErrorMessage(err);
      setError(errorMessage);
      if (!append) {
        toastr.error(errorMessage);
      }
      setRestaurants([]);
    } finally {
      setLoading(false);
      setLoadingMore(false);
    }
  }, [activeCategory, debouncedSearchValue, transformRestaurantData, restaurants.length]);

  // Fetch redemption tiers from API
  const fetchRedemptionTiers = useCallback(async () => {
    try {
      const response = await get(endpoints.pointsRedemptionTiers);
      const apiTiers = response.data.data || [];
      
      const transformedTiers = apiTiers
        .filter(tier => tier.is_active)
        .sort((a, b) => a.sort_order - b.sort_order)
        .map(tier => ({
          id: tier.id,
          points: tier.points_required,
          amount: tier.amount_cents / 100,
        }));
      
      setRedemptionTiers(transformedTiers);
    } catch (err) {
      console.error("Failed to fetch redemption tiers:", err);
      const errorMessage = extractErrorMessage(err);
      toastr.error(errorMessage);
      setRedemptionTiers([]);
    }
  }, []);

  // Create voucher via API
  const createVoucher = useCallback(async () => {
    if (!selectedRestaurant || !selectedVoucherTier) {
      setError("Please select a restaurant and voucher tier.");
      return false;
    }

    if (userPoints < selectedVoucherTier.points) {
      setError("Insufficient points for this voucher.");
      return false;
    }

    setLoading(true);
    setError(null);
    try {
      const payload = {
        points_redemption_tier_id: selectedVoucherTier.id,
        vendor_listing_id: selectedRestaurant.id,
      };

      const response = await post(endpoints.vouchers, payload);
      const voucherData = response.data.data.voucher;

      // Fetch updated points balance
      let pointsRemaining = userPoints - selectedVoucherTier.points;
      try {
        const balanceResponse = await get(endpoints.pointsBalance);
        pointsRemaining = balanceResponse.data.data.points || 0;
      } catch (balanceErr) {
        console.error("Failed to fetch updated balance:", balanceErr);
        // Fallback to calculated value
        pointsRemaining = userPoints - selectedVoucherTier.points;
      }

      const result = {
        amount: voucherData.amount_cents / 100,
        restaurantName: voucherData.vendor_listing?.title || selectedRestaurant.name,
        pointsRedeemed: voucherData.points || selectedVoucherTier.points,
        pointsRemaining,
      };

      setRedemptionResult(result);
      setCurrentStep(3);

      if (onVoucherCreated) {
        onVoucherCreated();
      }

      return true;
    } catch (err) {
      console.error("Failed to create voucher:", err);
      const errorMessage = extractErrorMessage(err);
      setError(errorMessage);
      toastr.error(errorMessage);
      return false;
    } finally {
      setLoading(false);
    }
  }, [selectedRestaurant, selectedVoucherTier, userPoints, onVoucherCreated]);

  // Debounce search value
  useEffect(() => {
    const timer = setTimeout(() => {
      setDebouncedSearchValue(searchValue);
    }, 500);
    return () => clearTimeout(timer);
  }, [searchValue]);

  // Fetch categories on mount
  useEffect(() => {
    if (categories.length === 0) {
      fetchCategories();
    }
  }, [categories.length, fetchCategories]);

  // Intersection Observer for horizontal category scroll
  /* eslint-disable react-hooks/exhaustive-deps */
  useEffect(() => {
    const sentinel = categorySentinelRef.current;
    if (!sentinel || !categoriesHasMore || categoriesLoadingMore) return;

    const observer = new IntersectionObserver(
      (entries) => {
        if (entries[0].isIntersecting) {
          loadMoreCategories();
        }
      },
      { threshold: 0.1, rootMargin: '100px' }
    );

    observer.observe(sentinel);
    return () => observer.disconnect();
  }, [categoriesHasMore, categoriesLoadingMore, categories.length]);
  /* eslint-enable react-hooks/exhaustive-deps */

  // Fetch restaurants when filters change
  /* eslint-disable react-hooks/exhaustive-deps */
  useEffect(() => {
    setCurrentPage(1);
    setHasMore(true);
    fetchRestaurants(1, false);
  }, [activeCategory, debouncedSearchValue]);
  /* eslint-enable react-hooks/exhaustive-deps */

  // Infinite scroll detection for restaurant list using Intersection Observer
  /* eslint-disable react-hooks/exhaustive-deps */
  useEffect(() => {
    const sentinel = restaurantSentinelRef.current;
    const scrollContainer = document.querySelector('.mc-select-list');
    
    if (!sentinel || !hasMore || loadingMore || loading || !scrollContainer) return;

    const observer = new IntersectionObserver(
      (entries) => {
        if (entries[0].isIntersecting) {
          fetchRestaurants(currentPage + 1, true);
        }
      },
      { 
        threshold: 0.1, 
        root: scrollContainer,
        rootMargin: '100px' 
      }
    );

    observer.observe(sentinel);
    return () => observer.disconnect();
  }, [hasMore, loadingMore, loading, currentPage]);
  /* eslint-enable react-hooks/exhaustive-deps */

  // Fetch redemption tiers on mount
  useEffect(() => {
    fetchRedemptionTiers();
  }, [fetchRedemptionTiers]);

  // Reset modal when closed
  useEffect(() => {
    if (!isOpen) {
      setCurrentStep(1);
      setSelectedRestaurant(null);
      setSelectedVoucherTier(null);
      setRedemptionResult(null);
      setError(null);
      setSearchValue("");
      setActiveCategory("all");
    } else {
      // If modal opens with a pre-selected restaurant, auto-select it and skip to step 2
      if (preSelectedRestaurant) {
        setSelectedRestaurant(preSelectedRestaurant);
        setCurrentStep(2);
      }
    }
  }, [isOpen, preSelectedRestaurant]);

  // Calculate voucher affordability
  const getVoucherOptions = useCallback(() => {
    return redemptionTiers.map(tier => ({
      id: tier.id,
      amount: tier.amount,
      pointsCost: tier.points,
      affordable: userPoints >= tier.points,
      pointsNeeded: Math.max(0, tier.points - userPoints),
    }));
  }, [redemptionTiers, userPoints]);

  const voucherOptions = getVoucherOptions();

  // Handle restaurant selection
  const handleSelectRestaurant = (restaurantId) => {
    const restaurant = restaurants.find(r => r.id === restaurantId);
    if (restaurant) {
      setSelectedRestaurant(restaurant);
      setCurrentStep(2);
    }
  };

  // Handle voucher selection
  const handleSelectVoucher = (voucherId) => {
    const voucher = voucherOptions.find(v => v.id === voucherId);
    if (voucher && voucher.affordable) {
      setSelectedVoucherTier(voucher);
    }
  };

  // Handle back navigation
  const handleBack = () => {
    if (currentStep === 2) {
      setCurrentStep(1);
      setSelectedRestaurant(null);
      setSelectedVoucherTier(null);
    }
  };

  // Handle confirmation
  const handleConfirm = () => {
    createVoucher();
  };

  // Handle go to wallet
  const handleGoToWallet = () => {
    onClose();
    if (onNavigateToWallet) {
      onNavigateToWallet();
    } else {
      // Fallback to window.location if no callback provided
      window.location.href = "/wallet";
    }
  };

  // Render Step 1: Select Restaurant
  const renderStep1 = () => (
    <>
      <StepHeader
        eyebrow="Step 1 of 2"
        title="Select a Restaurant"
        subtitle="Choose where you'd like to use your voucher."
        titleId="rvm-title"
      />

      <div className="mc-search">
        <input
          type="text"
          value={searchValue}
          onChange={(e) => setSearchValue(e.target.value)}
          placeholder="Search burgers, brunch, or happy hour..."
          aria-label="Search partner restaurants"
        />
        <img src={searchIcon} alt="" className="mc-search__icon" />
      </div>

      <HorizontalScroll className="mc-category-filters" role="tablist" aria-label="Filter by category">
        <div style={{ display: "flex", gap: "8px" }}>
          {categories.map((category) => {
            const isActive = category.id === activeCategory;
            const isImageUrl = category.icon && category.icon.startsWith("http");
            const hasImageError = categoryImageErrors[category.id];

            return (
              <button
                key={category.id}
                type="button"
                role="tab"
                aria-selected={isActive}
                className={`category-chip ${isActive ? "category-chip--active" : ""}`}
                onClick={() => setActiveCategory(category.id)}
              >
                <span className="category-chip__icon">
                  {isImageUrl && !hasImageError ? (
                    <img
                      src={category.icon}
                      alt={category.label}
                      onError={() => handleCategoryImageError(category.id)}
                    />
                  ) : category.icon && !isImageUrl ? (
                    <i className={`mdi ${category.icon}`} />
                  ) : (
                    <i className="mdi mdi-food" />
                  )}
                </span>
                {category.label}
              </button>
            );
          })}
          {categoriesHasMore && (
            <div
              ref={categorySentinelRef}
              style={{ width: '1px', height: '1px', flexShrink: 0 }}
              aria-hidden="true"
            />
          )}
          {categoriesLoadingMore && (
            <>
              {[...Array(12)].map((_, index) => (
                <div
                  key={`skeleton-${index}`}
                  className="category-chip category-chip--skeleton skeleton"
                  style={{
                    minWidth: "80px",
                    height: "40px",
                  }}
                />
              ))}
            </>
          )}
        </div>
      </HorizontalScroll>

      <div className="mc-select-list">
        {loading ? (
          <p style={{ textAlign: 'center', padding: '20px' }}>Loading restaurants...</p>
        ) : restaurants.length === 0 ? (
          <p style={{ textAlign: 'center', padding: '20px' }}>No restaurants found.</p>
        ) : (
          <>
            {restaurants.map((restaurant) => (
              <RestaurantOptionRow
                key={restaurant.id}
                restaurant={restaurant}
                selected={selectedRestaurant?.id === restaurant.id}
                onSelect={() => handleSelectRestaurant(restaurant.id)}
                imageError={restaurantImageErrors[restaurant.id]}
                onImageError={handleRestaurantImageError}
              />
            ))}
            {hasMore && (
              <div
                ref={restaurantSentinelRef}
                style={{ width: '1px', height: '1px' }}
                aria-hidden="true"
              />
            )}
            {loadingMore && (
              <p style={{ textAlign: 'center', padding: '20px' }}>Loading more restaurants...</p>
            )}
          </>
        )}
      </div>
    </>
  );

  // Render Step 2: Select Voucher
  const renderStep2 = () => (
    <>
      <StepHeader
        eyebrow="Step 2 of 2"
        title="Select Voucher"
        titleId="rvm-title"
      />

      <div className="mc-select-row">
        <span className="mc-select-row__thumb">
          <img 
            src={restaurantImageErrors[selectedRestaurant.id] ? fallbackImage : selectedRestaurant.image} 
            alt={selectedRestaurant.name}
            onError={() => handleRestaurantImageError(selectedRestaurant.id)}
          />
        </span>
        <div className="mc-select-row__body">
          <p className="mc-select-row__title">{selectedRestaurant.name}</p>
          <p className="mc-select-row__subtitle">
            {selectedRestaurant.category} &bull; {selectedRestaurant.location}
          </p>
        </div>
        <span className="mc-pill mc-pill--selected">Selected</span>
      </div>

      <InfoRow>
        <span className="mc-info-row__label--muted">Your Available Points:</span>
        <span className="mc-info-row__label">{userPoints.toLocaleString()}</span>
      </InfoRow>

      <div className="mc-select-list">
        {voucherOptions.map((option) => (
          <VoucherOptionRow
            key={option.id}
            option={option}
            selected={selectedVoucherTier?.id === option.id}
            onSelect={() => handleSelectVoucher(option.id)}
          />
        ))}
      </div>

      <div className="rvm-step2-buttons">
        <CustomButton
          type="button"
          variant="primary"
          iconPosition="right"
          icon={arrowRightIconAuth}
          className="w-100"
          onClick={handleConfirm}
          disabled={!selectedVoucherTier || loading}
        >
          {loading ? 'Creating...' : 'Confirm Redemption'}
        </CustomButton>

        <CustomButton
          type="button"
          variant="secondary"
          className="w-100"
          onClick={handleBack}
        >
          Back
        </CustomButton>
      </div>

      {error && (
        <p style={{ color: '#dc3545', textAlign: 'center', marginTop: '10px', fontSize: '14px' }}>
          {error}
        </p>
      )}
    </>
  );

  // Render Step 3: Success
  const renderStep3 = () => {
    if (!redemptionResult) {
      return (
        <div className="vam-wrap">
          <p className="mc-eyebrow">Redemption Successful</p>
          <SuccessIcon icon={giftIcon} tone="green" />
          <h2 className="mc-title mc-title--center" id="rvm-title">
            Voucher Added!
          </h2>
          <p className="mc-description mc-description--center">
            Your voucher has been added to your Wallet.
          </p>
          <CustomButton
            type="button"
            variant="primary"
            iconPosition="right"
            icon={arrowRightIconAuth}
            className="w-100"
            onClick={handleGoToWallet}
          >
            Go to Wallet
          </CustomButton>
        </div>
      );
    }

    return (
      <>
        <div className="vam-wrap">
          <p className="mc-eyebrow">Redemption Successful</p>

          <SuccessIcon icon={giftIcon} tone="green" />

          <h2 className="mc-title mc-title--center" id="rvm-title">
            Voucher Added!
          </h2>
          <p className="vam-highlight">
            Your ${redemptionResult.amount} voucher for {redemptionResult.restaurantName}
          </p>
          <p className="mc-description mc-description--center">
            Your voucher has been added to your Wallet. You can use it on your
            next visit to {redemptionResult.restaurantName}.
          </p>

          <InfoRow center>
            <span className="mc-info-row__label">
              {redemptionResult.pointsRedeemed?.toLocaleString() || 0} pts Redeemed
            </span>
            <span className="mc-info-row__dot">&bull;</span>
            <span className="mc-info-row__value">
              {redemptionResult.pointsRemaining?.toLocaleString() || 0} pts Remaining
            </span>
          </InfoRow>

          <CustomButton
            type="button"
            variant="primary"
            iconPosition="right"
            icon={arrowRightIconAuth}
            className="w-100"
            onClick={handleGoToWallet}
          >
            Go to Wallet
          </CustomButton>
        </div>
      </>
    );
  };

  return (
    <BottomSheetModal isOpen={isOpen} onClose={onClose} labelledBy="rvm-title">
      {currentStep === 1 && renderStep1()}
      {currentStep === 2 && renderStep2()}
      {currentStep === 3 && renderStep3()}
    </BottomSheetModal>
  );
};

RedeemVoucherModal.propTypes = {
  isOpen: PropTypes.bool.isRequired,
  onClose: PropTypes.func.isRequired,
  userPoints: PropTypes.number.isRequired,
  onVoucherCreated: PropTypes.func,
  onNavigateToWallet: PropTypes.func,
  preSelectedRestaurant: PropTypes.shape({
    id: PropTypes.oneOfType([PropTypes.string, PropTypes.number]).isRequired,
    name: PropTypes.string.isRequired,
    image: PropTypes.string.isRequired,
    category: PropTypes.string.isRequired,
    location: PropTypes.string.isRequired,
  }),
};

RedeemVoucherModal.defaultProps = {
  onVoucherCreated: () => {},
  onNavigateToWallet: null,
  preSelectedRestaurant: null,
};

export default RedeemVoucherModal;
